import { TranslatePipe } from '../../shared/i18n/translate.pipe';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LoadingService } from '../../shared/services/loading.service';
import { TypewriterDirective } from '../../shared/directives/typewriter.directive';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TranslationService } from '../../shared/i18n/translation.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [TranslatePipe, CommonModule, TypewriterDirective, RouterLink],
  templateUrl: './home.html',
  styleUrls: ['./home.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  // Liste des slides avec le contenu demandé
public slides: any[] = [
    {
      image: 'carousel/slide-1.jpg',
      title: 'L\'AGBE vous souhaite la bienvenue',
      text: 'Garder la foi, sauver une âme'
    },
    {
      image: 'carousel/slide-2.jpg',
      title: 'Comment mieux s\'intégrer en France ?',
      text: 'Découvrez nos programmes d\'accompagnement, de tutorat et nos ressources pour faciliter votre parcours.'
    },
    {
      // UTILISATION DE L'IMAGE LOCALE ICI 👇
      image: 'carousel/slide-3.jpg', 
      title: 'Soutenir l\'œuvre au Cameroun',
      text: 'Participez au développement socio-économique et soutenez nos actions missionnaires sur le terrain.'
    }
  ];

public events: any[] = [];

  public meetings: any[] = [];

  public actualites: any[] = [];

  public currentMainCarouselIndex: number = 0;
  private mainCarouselIntervalId: any;

  public currentEventCarouselIndex: number = 0;
  public currentMeetingCarouselIndex = 0;
  public currentNewsCarouselIndex = 0;
  private eventCarouselIntervalId: any;
  private contentRefreshIntervalId: any;

  constructor(
    private loadingService: LoadingService,
    private router: Router,
    private http: HttpClient,
    public i18n: TranslationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.refreshContent();
    if (isPlatformBrowser(this.platformId)) this.contentRefreshIntervalId = setInterval(() => this.refreshContent(), 30000);
    // Une fois que ton code estime que la page est "prête"
    setTimeout(() => {
      this.loadingService.finishLoading();
    }, 1500); // Temps du spinner, par exemple
    if (isPlatformBrowser(this.platformId)) {
      this.startMainCarousel();
      this.startEventCarousel();
    }
  }

  @HostListener('window:focus')
  refreshOnFocus(): void {
    if (isPlatformBrowser(this.platformId)) this.refreshContent();
  }

  private refreshContent(): void {
    const params = { _: Date.now().toString() };
    this.http.get<any[]>(`${environment.apiUrl}/carousel`, { params }).subscribe({
      next: rows => {
        this.slides = rows.length ? rows : this.slides;
        this.currentMainCarouselIndex = Math.min(this.currentMainCarouselIndex, Math.max(0, this.slides.length - 1));
        this.cdr.markForCheck();
      }
    });

    this.http.get<any[]>(`${environment.apiUrl}/events`, { params }).subscribe({
      next: rows => {
        this.events = rows.filter(row =>
          (row.event_type ?? 'event') === 'event' &&
          row.is_active !== false &&
          row.is_active !== 0
        );
        this.meetings = rows.filter(row =>
          row.event_type === 'meeting' &&
          row.is_active !== false &&
          row.is_active !== 0
        );
        this.currentEventCarouselIndex = this.validPageIndex(this.events, this.currentEventCarouselIndex);
        this.currentMeetingCarouselIndex = this.validPageIndex(this.meetings, this.currentMeetingCarouselIndex);
        this.cdr.markForCheck();
      }
    });

    this.http.get<any[]>(`${environment.apiUrl}/news`, { params }).subscribe({
      next: rows => {
        this.actualites = rows;
        this.currentNewsCarouselIndex = this.validPageIndex(this.actualites, this.currentNewsCarouselIndex);
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy() {
    this.clearMainCarousel();
    this.clearEventCarousel();
    if (this.contentRefreshIntervalId) clearInterval(this.contentRefreshIntervalId);
  }
  // Démarre le défilement automatique (toutes les 6 secondes)
  startMainCarousel() {
    this.mainCarouselIntervalId = setInterval(() => {
      this.nextSlide();
    }, 6000); 
  }

  startEventCarousel() {
    // Chaque vue contient au maximum trois cartes.
    this.eventCarouselIntervalId = setInterval(() => {
      if (this.events.length > 1) this.currentEventCarouselIndex = (this.currentEventCarouselIndex + 1) % this.events.length;
      if (this.meetings.length > 1) this.currentMeetingCarouselIndex = (this.currentMeetingCarouselIndex + 1) % this.meetings.length;
      if (this.actualites.length > 1) this.currentNewsCarouselIndex = (this.currentNewsCarouselIndex + 1) % this.actualites.length;
      this.cdr.markForCheck();
    }, 6000);
  }

  clearMainCarousel() {
    if (this.mainCarouselIntervalId) {
      clearInterval(this.mainCarouselIntervalId);
    }
  }
  
  clearEventCarousel() {
    if (this.eventCarouselIntervalId) {
      clearInterval(this.eventCarouselIntervalId);
    }
  }

  nextSlide() {
    this.currentMainCarouselIndex = (this.currentMainCarouselIndex + 1) % this.slides.length;
    this.cdr.markForCheck();
  }

  prevSlide() {
    this.currentMainCarouselIndex = (this.currentMainCarouselIndex - 1 + this.slides.length) % this.slides.length;
    this.cdr.markForCheck();
  }

  // Permet de cliquer sur les petits points en bas
  goToSlide(index: number) {
    this.currentMainCarouselIndex = index;
    this.clearMainCarousel();
    this.startMainCarousel(); // Réinitialise le chrono
  }

  localized(item: any, field: string): string { return item[`${field}_${this.i18n.language()}`] ?? item[field] ?? item[`${field}_fr`] ?? ''; }
  slideImage(slide: any): string { return this.mediaUrl(slide.image_url ?? slide.image ?? ''); }
  mediaUrl(value: unknown): string {
    const url = String(value ?? '').trim();
    if (!url) return '';
    if (/^www\./i.test(url)) return `https://${url}`;
    if (/^http:\/\//i.test(url)) return url.replace(/^http:\/\//i, 'https://');
    if (url.startsWith('/uploads/')) return `${environment.apiUrl.replace(/\/$/, '')}${url}`;
    return url;
  }
  hideBrokenImage(event: Event): void { (event.target as HTMLImageElement).style.display = 'none'; }
  eventInfo(event: any): string { return this.localized(event, 'info'); }
  eventDescription(event: any): string { return this.localized(event, 'description') || event.desc || ''; }
  showEventDescription(event: any): boolean {
    const info = this.eventInfo(event).trim();
    const description = this.eventDescription(event).trim();
    return description !== '' && description !== info;
  }
  eventUrl(event: any): string { return event.registration_url ?? event.helloAssoUrl ?? '#'; }
  eventPageUrl(event: any): string { return `/evenements/${event.slug ?? event.id}`; }
  newsDate(item: any): string { return item.published_at ? new Date(item.published_at).toLocaleDateString(this.i18n.language()) : item.date; }
  newsUrl(item: any): string { return item.slug ? `/actualites/${item.slug}` : '#'; }
  eventDay(event: any): string { return event.starts_at ? new Date(event.starts_at).getDate().toString().padStart(2, '0') : event.day; }
  eventMonth(event: any): string { return event.starts_at ? new Intl.DateTimeFormat(this.i18n.language(), { month: 'short' }).format(new Date(event.starts_at)).toUpperCase() : event.month; }
  visibleEvents(): any[] { return this.window(this.events, this.currentEventCarouselIndex); }
  visibleMeetings(): any[] { return this.window(this.meetings, this.currentMeetingCarouselIndex); }
  visibleNews(): any[] { return this.window(this.actualites, this.currentNewsCarouselIndex); }
  pageIndexes(items: any[]): number[] { return Array.from({ length: items.length }, (_, index) => index); }
  changePage(kind: 'events' | 'meetings' | 'news', direction: number): void {
    const list = kind === 'events' ? this.events : kind === 'meetings' ? this.meetings : this.actualites;
    const pages = list.length;
    if (pages < 2) return;
    const key = kind === 'events' ? 'currentEventCarouselIndex' : kind === 'meetings' ? 'currentMeetingCarouselIndex' : 'currentNewsCarouselIndex';
    (this as any)[key] = ((this as any)[key] + direction + pages) % pages;
    this.cdr.markForCheck();
  }
  private window(items: any[], index: number): any[] {
    if (items.length <= 3) {
      return items.map((_, offset) => items[(index + offset) % items.length]);
    }
    return Array.from({ length: 3 }, (_, offset) => items[(index + offset) % items.length]);
  }
  private validPageIndex(items: any[], current: number): number {
    return Math.min(current, Math.max(0, items.length - 1));
  }
}   
